// Source-attributed extraction merge (SOR-223 — applyExtraction merges, not
// replaces).
//
// `applyExtraction` used to REPLACE the wizard's party/attorney arrays wholesale
// with entries minted from the OCR ExtractedPatch, so applying an extraction
// dropped every case-roster/manual entry and re-extracting multiplied them. This
// lib converts that to a source-attributed MERGE: every existing entry is copied
// through UNCHANGED (preserving its source, representedPartyIds, and every other
// field), and only a genuinely-new patch entry — one whose identity matches no
// existing entry — is appended, tagged `extraction`.
//
// Identity is the dedup key spec'd by SPEC-EFILING R5 (sameIdentity): bar number
// when present else normalized name for attorneys; normalized name + role for
// parties. The `norm` / name-key rules MIRROR `dedupe-party-roster.ts` and
// `remap-metadata-references.ts` — the codebase deliberately keeps those
// reconcilers as parallel mirrors of the same rules, and this is a third mirror
// rather than an import of their module-private helpers (whose property suites
// are non-goal-protected).
//
// R7 falls out structurally: a matching incoming attorney is SKIPPED (never
// merged field-by-field onto the survivor), so an extraction-minted attorney's
// empty `representedPartyIds` can never overwrite a case-roster attorney's real
// linkage. Do not "improve" this by copying fields from a matched candidate onto
// the survivor — that would reintroduce the clobber.
//
// Pure and side-effect-free apart from the injected id generator (default
// `crypto.randomUUID`), mirroring `hydrate-case-roster.ts`.
// OCR role string → JTI role code. Mirrors the bootstrap that used to live inline
// in EFilingContext.applyExtraction.
const OCR_ROLE_BOOTSTRAP = {
    'plaintiff': 'PLAIN',
    'defendant': 'DEF',
    'petitioner': 'PET',
    'respondent': 'RES',
    'cross-complainant': 'XCMPLNT',
    'cross-defendant': 'XDEF',
    'other': 'OTHER',
};
const ROLE_LABELS = {
    'PLAIN': 'Plaintiff',
    'DEF': 'Defendant',
    'PET': 'Petitioner',
    'RES': 'Respondent',
    'XCMPLNT': 'Cross-Complainant',
    'XDEF': 'Cross-Defendant',
    'OTHER': 'Other',
};
function parseFullName(fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length <= 1)
        return { firstName: parts[0] || '', lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}
// Mirrors dedupe-party-roster.ts / remap-metadata-references.ts `norm`
// (collapse \s+, trim, lowercase).
const norm = (s) => (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
// Party identity (mirrors the dedupe key): organization name wins else first +
// last, suffixed by the normalized role code so same-name / different-role
// parties stay distinct. Computed off the MINTED entry, so the role part is the
// post-bootstrap code (e.g. 'PLAIN'), matching a prior roster entry's roleCode.
const partyKey = (p) => `${norm(p.organizationName) || norm(`${p.firstName ?? ''} ${p.lastName ?? ''}`)}|${norm(p.roleCode)}`;
// Attorney identity (mirrors remap-metadata-references.ts `attorneyNameKey`): bar
// number when present, else normalized first + last. The `||` fallback is
// load-bearing — the mint sets `barNumber: a.barNumber || ''`, so a bar-less
// attorney falls through to the name key instead of colliding on an empty bar.
const attorneyKey = (a) => norm(a.barNumber) || norm(`${a.firstName ?? ''} ${a.lastName ?? ''}`);
/**
 * Merge OCR-extracted parties onto the prior wizard party roster.
 *
 * Returns `prior` UNCHANGED (same reference) when the patch omits parties (R2),
 * or when every incoming party matches an existing entry. Otherwise returns
 * `[...prior, ...additions]` where each addition is a freshly-minted party tagged
 * `source: 'extraction'`. Existing entries are never overwritten or duplicated
 * (R4/R5); a matching incoming party is skipped.
 *
 * @param newId injectable id generator (default `crypto.randomUUID`) — lets tests
 *   assert minted ids deterministically.
 */
export function mergeExtractedParties(prior, patchParties, newId = () => crypto.randomUUID()) {
    if (!patchParties?.length)
        return prior;
    const seen = new Set(prior.map(partyKey));
    const additions = [];
    for (const p of patchParties) {
        const roleCode = OCR_ROLE_BOOTSTRAP[p.role] ?? 'OTHER';
        const roleName = ROLE_LABELS[roleCode] ?? 'Other';
        const parsed = parseFullName(p.name);
        const minted = {
            id: newId(),
            partyType: p.type,
            roleCode,
            roleName,
            ...(p.type === 'person'
                ? { firstName: parsed.firstName, lastName: parsed.lastName }
                : { organizationName: p.name }),
            partySubTypes: [],
            isLeadClient: false,
            isNewToCase: false,
            feeExemptionType: 'NONE',
            requestFeesExemption: false,
            feeWaiverOnFile: false,
            eServiceConsent: false,
            isSelfRepresented: false,
            hasAlternateNames: false,
            alternateNames: [],
            requiresInterpreter: false,
            source: 'extraction',
        };
        const key = partyKey(minted);
        if (seen.has(key))
            continue;
        seen.add(key);
        additions.push(minted);
    }
    return additions.length ? [...prior, ...additions] : prior;
}
/**
 * Merge OCR-extracted attorneys onto the prior wizard attorney roster.
 *
 * Same contract as {@link mergeExtractedParties}: `prior` returned unchanged when
 * the patch omits attorneys (R2) or every incoming attorney matches; new entries
 * appended `source: 'extraction'` with empty `representedPartyIds`. Because a
 * matching incoming attorney is skipped (not merged), an extraction-minted
 * attorney's empty linkage never overwrites a case-roster attorney's real
 * `representedPartyIds` (R7).
 */
export function mergeExtractedAttorneys(prior, patchAttorneys, newId = () => crypto.randomUUID()) {
    if (!patchAttorneys?.length)
        return prior;
    const seen = new Set(prior.map(attorneyKey));
    const additions = [];
    for (const a of patchAttorneys) {
        const { firstName, lastName } = parseFullName(a.fullName);
        const minted = {
            id: newId(),
            firstName,
            lastName,
            barState: a.barState || 'CA',
            barNumber: a.barNumber || '',
            firmName: a.firm || '',
            representedPartyIds: [],
            source: 'extraction',
        };
        const key = attorneyKey(minted);
        if (seen.has(key))
            continue;
        seen.add(key);
        additions.push(minted);
    }
    return additions.length ? [...prior, ...additions] : prior;
}
/**
 * Backfill a deterministic `source` onto persisted entries during draft hydration
 * (R9): an entry that already carries a tag is preserved unchanged (same
 * reference); a legacy untagged entry defaults to `manual`. Generic over both
 * party and attorney entries.
 */
export function withDefaultSource(entries) {
    return entries.map(e => (e.source ? e : { ...e, source: 'manual' }));
}
